// v3.18-1376 GMAIL-001 — Google Mailbox Connection surface.
//
// This is the OAuth foundation only. No mailbox sync, no compose,
// no inbox. Ops connect a Google Workspace mailbox here so future
// sprints can lift Gmail read/send off the account.
//
// SMTP2GO remains authoritative for every automated outbound email.
// Nothing here alters that.
import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Loader2, Mail, ShieldCheck, Trash2, ExternalLink, RefreshCw, AlertCircle, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { Button } from '../components/ui/button';

const fmtDate = (iso) => (iso ? iso.slice(0, 19).replace('T', ' ') : '—');

export default function GoogleMailboxesPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [disconnectingId, setDisconnectingId] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/google/mailboxes');
      setItems(data?.items || []);
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Failed to load mailboxes';
      toast.error(msg);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Callback bounce handler: /google-mailboxes?gmail_status=connected&email=...
  useEffect(() => {
    const q = new URLSearchParams(location.search);
    const status = q.get('gmail_status');
    if (!status) return;
    if (status === 'connected') {
      toast.success(`Connected ${q.get('email') || 'mailbox'}`);
    } else if (status === 'denied') {
      toast.error('Google access was denied');
    } else if (status === 'error') {
      toast.error(`OAuth error: ${q.get('error') || 'unknown'}`);
    }
    navigate('/google-mailboxes', { replace: true });
    load();

  }, [location.search]);

  const startConnect = async () => {
    setConnecting(true);
    try {
      const { data } = await api.get('/google/connect');
      if (data?.authorization_url) {
        window.location.href = data.authorization_url;
      } else {
        toast.error('Could not build authorization URL');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Connect failed');
    } finally { setConnecting(false); }
  };

  const disconnect = async (row) => {
    if (!window.confirm(`Disconnect ${row.email_address}?`)) return;
    setDisconnectingId(row.id);
    try {
      await api.delete(`/google/mailboxes/${row.id}`);
      toast.success(`${row.email_address} disconnected`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Disconnect failed');
    } finally { setDisconnectingId(null); }
  };

  const statusPill = (s) => {
    const map = {
      connected: 'bg-emerald-50 text-emerald-700 border-emerald-200',
      revoked:   'bg-rose-50 text-rose-700 border-rose-200',
      expired:   'bg-amber-50 text-amber-700 border-amber-200',
    };
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold border ${map[s] || map.connected}`}>
        {s === 'revoked'
          ? <AlertCircle className="w-3 h-3" />
          : <CheckCircle2 className="w-3 h-3" />}
        {s || 'connected'}
      </span>
    );
  };

  return (
    <div className="space-y-5" data-testid="google-mailboxes-page">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-slate-900 text-white flex items-center justify-center">
            <Mail className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-2xl font-extrabold text-slate-900">Google Mailbox Connections</h1>
            <p className="text-sm text-slate-500">
              Connect one or more Google Workspace mailboxes for the upcoming Gmail integration. This
              is the OAuth foundation only — mailbox sync, compose, and inbox arrive in later sprints.
            </p>
          </div>
        </div>
        <Button
          data-testid="gmail-connect-btn"
          onClick={startConnect}
          disabled={connecting}
          className="bg-slate-900 hover:bg-black text-white"
        >
          {connecting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ExternalLink className="w-4 h-4 mr-2" />}
          Connect a Google account
        </Button>
      </div>

      <div className="flex items-start gap-3 p-4 rounded-2xl bg-amber-50 border border-amber-200 text-amber-800 text-xs" data-testid="gmail-smtp2go-banner">
        <ShieldCheck className="w-4 h-4 mt-0.5 flex-shrink-0" />
        <div>
          <b>SMTP2GO is untouched.</b> Every automated outbound email — appointment reminders,
          magic links, contract deliveries, payment confirmations, system notifications —
          continues to flow through the existing SMTP2GO path exactly as it does today.
          Connecting a Google mailbox here does <b>not</b> reroute any of that traffic.
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
          <div className="text-sm font-semibold text-slate-800">Connected mailboxes</div>
          <button
            onClick={load}
            data-testid="gmail-refresh"
            className="text-xs text-slate-500 hover:text-slate-800 inline-flex items-center gap-1"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
        </div>

        {loading ? (
          <div className="p-10 flex items-center justify-center text-slate-500 text-sm">
            <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…
          </div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center text-sm text-slate-500" data-testid="gmail-empty">
            No mailboxes connected yet. Click <b>Connect a Google account</b> above to start.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 uppercase text-[11px] tracking-wider">
              <tr>
                <th className="text-left px-4 py-2">Email</th>
                <th className="text-left px-4 py-2">Display name</th>
                <th className="text-left px-4 py-2">Status</th>
                <th className="text-left px-4 py-2">Connected</th>
                <th className="text-left px-4 py-2">Token expires</th>
                <th className="text-right px-4 py-2">Action</th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr
                  key={r.id}
                  data-testid={`gmail-row-${r.id}`}
                  className="border-t border-slate-100"
                >
                  <td className="px-4 py-3 font-mono text-slate-800">{r.email_address}</td>
                  <td className="px-4 py-3 text-slate-600">{r.display_name || '—'}</td>
                  <td className="px-4 py-3">{statusPill(r.status)}</td>
                  <td className="px-4 py-3 text-xs text-slate-500 whitespace-nowrap">
                    {fmtDate(r.connected_at)}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-500 whitespace-nowrap">
                    {fmtDate(r.token_expiration)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      data-testid={`gmail-disconnect-${r.id}`}
                      onClick={() => disconnect(r)}
                      disabled={disconnectingId === r.id}
                      className="inline-flex items-center gap-1 text-rose-600 hover:text-rose-800 text-xs font-semibold disabled:opacity-50"
                    >
                      {disconnectingId === r.id
                        ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        : <Trash2 className="w-3.5 h-3.5" />}
                      Disconnect
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
